import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { LocalLineItem } from '../line-items/hooks/useLocalLineItems';
import { AdjustmentSettings } from '@/types/adjustment-filters';
import { useQueryClient } from '@tanstack/react-query';
import { useImpersonationGuard } from '@/hooks/useImpersonationGuard';

interface CommitChangesParams {
  estimateId: string;
  localLineItems: LocalLineItem[];
  baselineSnapshot: LocalLineItem[];
  adjustmentSettings: AdjustmentSettings;
  finalStepFields: {
    special_instructions: string;
    payment_terms: string;
    validity_period: number;
  };
}

export const useEstimateCommit = () => {
  const [isCommitting, setIsCommitting] = useState(false);
  const queryClient = useQueryClient();
  const { guardWrite } = useImpersonationGuard();

  // Commit all changes to the database
  const commitChanges = useCallback(async ({
    estimateId,
    localLineItems,
    baselineSnapshot,
    adjustmentSettings,
    finalStepFields,
  }: CommitChangesParams): Promise<{ success: boolean; error?: string }> => {
    if (!guardWrite('commit estimate changes')) return { success: false, error: 'View Only mode' };
    setIsCommitting(true);
    
    try {
      // 1. Handle line item deletions
      const deletions = localLineItems.filter(li => !li._isNew && li._isDeleted);
      if (deletions.length > 0) {
        const { error: deleteError } = await supabase
          .from('estimate_line_items')
          .delete()
          .in('id', deletions.map(d => d.id));
        
        if (deleteError) throw deleteError;
      }

      // 2. Handle line item additions
      const additions = localLineItems.filter(li => li._isNew && !li._isDeleted);
      if (additions.length > 0) {
        const insertData = additions.map(item => ({
          estimate_id: estimateId,
          component_type: item.component_type,
          component_id: item.component_id,
          description: item.description,
          base_unit: item.base_unit,
          quantity_in_base_units: item.quantity_in_base_units,
          unit_cost_snapshot: item.unit_cost_snapshot,
          total_cost_snapshot: item.total_cost_snapshot,
          sort_order: item.sort_order,
          manual_override: item.manual_override || false,
          manual_unit_cost: item.manual_unit_cost || null,
          manual_total_cost: item.manual_total_cost || null,
          manual_description: item.manual_description || null,
          original_description: item.original_description || null,
          category_id: item.category_id || null,
        }));

        const { error: insertError } = await supabase
          .from('estimate_line_items')
          .insert(insertData);
        
        if (insertError) throw insertError;
      }

      // 3. Handle line item modifications
      const modifications = localLineItems.filter(li => {
        if (li._isNew || li._isDeleted) return false;
        
        const original = baselineSnapshot.find(b => b.id === li.id);
        if (!original) return false;
        
        return (
          li.quantity_in_base_units !== original.quantity_in_base_units ||
          li.unit_cost_snapshot !== original.unit_cost_snapshot ||
          li.description !== original.description ||
          li.manual_override !== original.manual_override ||
          li.manual_unit_cost !== original.manual_unit_cost
        );
      });

      for (const item of modifications) {
        const { error: updateError } = await supabase
          .from('estimate_line_items')
          .update({
            quantity_in_base_units: item.quantity_in_base_units,
            unit_cost_snapshot: item.unit_cost_snapshot,
            total_cost_snapshot: item.total_cost_snapshot,
            description: item.description,
            manual_override: item.manual_override || false,
            manual_unit_cost: item.manual_unit_cost || null,
            manual_total_cost: item.manual_total_cost || null,
            manual_description: item.manual_description || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', item.id);
        
        if (updateError) throw updateError;
      }

      // 4. Update estimate with final step fields and adjustments
      const { error: estimateError } = await supabase
        .from('estimates')
        .update({
          special_instructions: finalStepFields.special_instructions || null,
          payment_terms: finalStepFields.payment_terms || null,
          validity_period: finalStepFields.validity_period,
          adjustment_metadata: adjustmentSettings as any,
          updated_at: new Date().toISOString(),
        })
        .eq('id', estimateId);

      if (estimateError) throw estimateError;

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['estimateLineItems', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });

      return { success: true };
    } catch (error: any) {
      console.error('Error committing changes:', error);
      return { success: false, error: error.message || 'Failed to save changes' };
    } finally {
      setIsCommitting(false);
    }
  }, [queryClient]);

  return {
    isCommitting,
    commitChanges,
  };
};
